import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API } from "../lib/api";
import axios from "axios";
import { Anvil, MapPin, Star, ShieldCheck, Phone, Mail, Globe, ArrowLeft, BadgeCheck, Loader2, Image as ImageIcon } from "lucide-react";

const REAL_TYPES = { bache: "Bâcherie", sellerie_auto: "Sellerie auto", sellerie_marine: "Sellerie marine", coussin: "Coussins", capote: "Capotes / Tauds", cuir: "Cuir", habitat: "Habitat / Déco", autre: "Autres" };

function RealisationsBlock({ realisations }) {
  const [active, setActive] = useState("all");
  if (!realisations || realisations.length === 0) return null;
  const types = [...new Set(realisations.map((r) => r.type || "autre"))];
  const shown = active === "all" ? realisations : realisations.filter((r) => (r.type || "autre") === active);
  const pill = (on) => `px-3 py-1.5 rounded-full text-sm font-medium transition-all ${on ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="artisan-realisations">
      <h2 className="font-display font-semibold text-navy text-lg flex items-center gap-2"><ImageIcon className="w-5 h-5 text-sky-500" /> Réalisations</h2>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="realisation-tabs">
        <button onClick={() => setActive("all")} data-testid="realisation-tab-all" className={pill(active === "all")}>Toutes</button>
        {types.map((t) => <button key={t} onClick={() => setActive(t)} data-testid={`realisation-tab-${t}`} className={pill(active === t)}>{REAL_TYPES[t] || t}</button>)}
      </div>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shown.map((r) => (
          <figure key={r.id} className="rounded-xl overflow-hidden border border-slate-100 group" data-testid={`realisation-${r.id}`}>
            <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
              <img src={r.image} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <figcaption className="p-3">
              <div className="text-sm font-semibold text-navy">{r.title}</div>
              {r.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.description}</div>}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

const initials = (n) => (n || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("fr-FR", { year: "numeric", month: "long" }); } catch { return ""; } };

function Stars({ value, size = "w-5 h-5" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

function setJsonLd(obj) {
  let el = document.getElementById("artisan-jsonld");
  if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "artisan-jsonld"; document.head.appendChild(el); }
  el.textContent = JSON.stringify(obj);
}

export default function PublicArtisan() {
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/directory/${companyId}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [companyId]);

  useEffect(() => {
    if (!data) return;
    const c = data.company;
    document.title = `${c.name} — sellier-garnisseur ${c.city || ""} | Avis & fiche`;
    setJsonLd({
      "@context": "https://schema.org", "@type": "LocalBusiness", name: c.name,
      address: { "@type": "PostalAddress", addressLocality: c.city, postalCode: c.zip, addressCountry: "FR" },
      telephone: data.profile.phone || undefined, url: data.profile.website || undefined,
      aggregateRating: data.aggregate.count > 0 ? {
        "@type": "AggregateRating", ratingValue: data.aggregate.global, reviewCount: data.aggregate.count, bestRating: 5,
      } : undefined,
      review: (data.reviews || []).slice(0, 10).map((r) => ({
        "@type": "Review", author: { "@type": "Person", name: r.client_name },
        reviewRating: { "@type": "Rating", ratingValue: r.global, bestRating: 5 }, reviewBody: r.comment,
      })),
    });
  }, [data]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500">Fiche introuvable.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;

  const { company: c, profile, aggregate, criteria, reviews, pages } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-navy text-white">
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/annuaire" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><ArrowLeft className="w-4 h-4" /> Annuaire</Link>
          <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-sky-400 flex items-center justify-center"><Anvil className="w-4 h-4 text-navy" /></div><span className="font-display font-bold">Fabriko</span></div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-8 space-y-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="artisan-header">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-navy text-sky-300 flex items-center justify-center font-display font-bold text-xl shrink-0">{initials(c.name)}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-display font-bold text-navy">{c.name}</h1>
                {c.member_sts && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 text-xs font-bold"><ShieldCheck className="w-3.5 h-3.5" /> Réseau STS</span>}
              </div>
              {c.city && <div className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-4 h-4" /> {[c.zip, c.city].filter(Boolean).join(" ")}</div>}
              <div className="flex items-center gap-2 mt-2">
                <Stars value={aggregate.global} />
                <span className="text-lg font-bold text-navy">{aggregate.global || "—"}</span>
                <span className="text-sm text-slate-400">({aggregate.count} avis vérifiés)</span>
              </div>
            </div>
          </div>
          {c.description_public && <p className="text-slate-600 mt-4 leading-relaxed">{c.description_public}</p>}
          {c.specialties?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {c.specialties.map((s) => <span key={s} className="px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-sm">{s}</span>)}
            </div>
          )}
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-600">
            {profile.phone && <a href={`tel:${profile.phone}`} className="inline-flex items-center gap-1.5 hover:text-sky-600"><Phone className="w-4 h-4" /> {profile.phone}</a>}
            {profile.email && <a href={`mailto:${profile.email}`} className="inline-flex items-center gap-1.5 hover:text-sky-600"><Mail className="w-4 h-4" /> {profile.email}</a>}
            {profile.website && <a href={profile.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-sky-600"><Globe className="w-4 h-4" /> Site web</a>}
          </div>
          {pages?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {pages.map((p) => <Link key={p.slug} to={`/site/${c.id}/page/${p.slug}`} className="text-sm text-sky-600 hover:underline">{p.title} →</Link>)}
            </div>
          )}
        </div>

        <RealisationsBlock realisations={data.realisations} />

        {/* Criteria breakdown */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="artisan-criteria">
          <h2 className="font-display font-semibold text-navy text-lg">Notes détaillées</h2>
          <div className="mt-4 space-y-3">
            {criteria.map((cr) => (
              <div key={cr.key} data-testid={`crit-${cr.key}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-600">{cr.label}</span>
                  <span className="font-semibold text-navy">{aggregate.criteria[cr.key] || 0} / 5</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full bg-sky-400 rounded-full" style={{ width: `${(aggregate.criteria[cr.key] || 0) / 5 * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reviews */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="artisan-reviews">
          <h2 className="font-display font-semibold text-navy text-lg">Avis clients ({aggregate.count})</h2>
          <div className="mt-4 divide-y divide-slate-100">
            {reviews.map((r) => (
              <div key={r.id} className="py-4" data-testid={`review-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-navy">{r.client_name}</span>
                    {r.verified && <span className="inline-flex items-center gap-0.5 text-[11px] text-emerald-600 font-semibold"><BadgeCheck className="w-3.5 h-3.5" /> Vérifié</span>}
                  </div>
                  <div className="flex items-center gap-2"><Stars value={r.global} size="w-4 h-4" /><span className="text-sm font-semibold text-navy">{r.global}</span></div>
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-2">{r.comment}</p>}
                {r.reply && (
                  <div className="mt-2 ml-4 pl-3 border-l-2 border-sky-200 bg-sky-50/50 rounded-r-lg p-2" data-testid={`reply-${r.id}`}>
                    <div className="text-xs font-semibold text-sky-700">Réponse de {c.name}</div>
                    <p className="text-sm text-slate-600 mt-0.5">{r.reply}</p>
                  </div>
                )}
                <div className="text-xs text-slate-400 mt-1">{fmtDate(r.created_at)}</div>
              </div>
            ))}
            {reviews.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Pas encore d'avis.</p>}
          </div>
        </div>
      </main>

      <footer className="bg-navy text-slate-300">
        <div className="max-w-4xl mx-auto px-5 py-8 text-center text-sm">Fiche publiée sur l'annuaire Fabriko</div>
      </footer>
    </div>
  );
}
